import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Skeleton,
  Alert
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { getTeamPatterns } from '../../utils/analyticsApi';

const TeamVotingPatterns = ({ sessionId, scope, organizationId, teamId }) => {
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPatterns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, scope, organizationId, teamId]);

  const fetchPatterns = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (sessionId) params.sessionId = sessionId;
      if (organizationId) params.organizationId = organizationId;
      if (teamId) params.teamId = teamId;
      const response = await getTeamPatterns(params);
      setPatterns(response.data.patterns || []);
    } catch (err) {
      console.error('Error fetching team patterns:', err);
      setError('Failed to load team voting patterns');
    } finally {
      setLoading(false);
    }
  };

  const getBarColor = (consensusRate) => {
    if (consensusRate >= 80) return '#10B981'; // Green
    if (consensusRate >= 60) return '#F59E0B'; // Yellow
    return '#EF4444'; // Red
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="subtitle2">Estimate: {data.estimate}</Typography>
          <Typography variant="body2" color="text.secondary">
            Items: {data.totalItems}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Avg Consensus: {data.averageConsensusRate}%
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', gap: 1, fontSize: '0.75rem' }}>
            <span style={{ color: '#10B981' }}>High: {data.highConsensusCount}</span>
            <span style={{ color: '#F59E0B' }}>Med: {data.mediumConsensusCount}</span>
            <span style={{ color: '#EF4444' }}>Low: {data.lowConsensusCount}</span>
          </Box>
        </Paper>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Team Voting Patterns
        </Typography>
        <Skeleton variant="rectangular" height={300} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (patterns.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          No voting data available yet
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Consensus Rate by Estimate Value
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        How often team members agree on estimates for each point value
      </Typography>

      <Paper sx={{ p: 2 }}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={patterns} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis
              dataKey="estimate"
              tick={{ fill: '#64748B', fontSize: 12 }}
              axisLine={{ stroke: '#CBD5E1' }}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fill: '#64748B', fontSize: 12 }}
              axisLine={{ stroke: '#CBD5E1' }}
              tickFormatter={(value) => `${value}%`}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <Bar dataKey="averageConsensusRate" name="Consensus Rate" radius={[4, 4, 0, 0]}>
              {patterns.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getBarColor(entry.averageConsensusRate)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: '#10B981' }} />
          <Typography variant="caption" color="text.secondary">
            High ({'>'}80%)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: '#F59E0B' }} />
          <Typography variant="caption" color="text.secondary">
            Medium (60-80%)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: '#EF4444' }} />
          <Typography variant="caption" color="text.secondary">
            Low ({'<'}60%)
          </Typography>
        </Box>
      </Box>

      {/* Summary table */}
      <Paper sx={{ mt: 3, overflow: 'hidden' }}>
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            '& th, & td': {
              p: 1.5,
              textAlign: 'left',
              borderBottom: '1px solid',
              borderColor: 'divider'
            },
            '& th': {
              bgcolor: '#141414',
              fontWeight: 600,
              fontSize: '0.875rem'
            },
            '& td': {
              fontSize: '0.875rem'
            }
          }}
        >
          <thead>
            <tr>
              <th>Estimate</th>
              <th>Items</th>
              <th>Avg Consensus</th>
              <th>High</th>
              <th>Medium</th>
              <th>Low</th>
            </tr>
          </thead>
          <tbody>
            {patterns.map((pattern) => (
              <tr key={pattern.estimate}>
                <td>
                  <Typography variant="body2" fontWeight={500}>
                    {pattern.estimate}
                  </Typography>
                </td>
                <td>{pattern.totalItems}</td>
                <td>
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: pattern.averageConsensusRate >= 80
                        ? 'success.light'
                        : pattern.averageConsensusRate >= 60
                        ? 'warning.light'
                        : 'error.light',
                      color: pattern.averageConsensusRate >= 80
                        ? 'success.dark'
                        : pattern.averageConsensusRate >= 60
                        ? 'warning.dark'
                        : 'error.dark'
                    }}
                  >
                    {pattern.averageConsensusRate}%
                  </Box>
                </td>
                <td style={{ color: '#10B981' }}>{pattern.highConsensusCount}</td>
                <td style={{ color: '#F59E0B' }}>{pattern.mediumConsensusCount}</td>
                <td style={{ color: '#EF4444' }}>{pattern.lowConsensusCount}</td>
              </tr>
            ))}
          </tbody>
        </Box>
      </Paper>
    </Box>
  );
};

export default TeamVotingPatterns;
